import React, { useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import {
    Card,
    CardBody,
    Col,
    Container,
    FormGroup,
    Input,
    Label,
    Row
} from 'reactstrap';

import colors from '../../default/Colors';
import { Register } from '../../models/register';
import Swal from 'sweetalert2';
import { useDispatch, useSelector } from 'react-redux';
import * as RegisterStore from '../../store/Register';
import { AppState } from '../../models/AppState';
import { ApplicationState } from '../../store';
import { hideLoader, showLoader } from '../../common/helpers/loader';
import {safeParseJSON} from "../../common/helpers/format";

type RouteState = {
    redirect?: string;
    modelRegister?: Register;
};

type FormField = {
    lg?: { size: number; offset?: number };
    label: string;
    name: string;
    type: string;
    asterisk?: boolean;
    disabled?: boolean;
};

const FORM_LAYOUT: FormField[][] = [
    [
        {
            label: 'Employee Code',
            name: 'employee_code',
            type: 'text',
            asterisk: true,
        },
        {
            label: 'User ID',
            name: 'user_id',
            type: 'text',
            asterisk: true,
            disabled: true
        }
    ],
    [
        {
            lg: { size: 2 },
            label: 'Prefix',
            name: 'prefix',
            type: 'text',
            asterisk: true,
            disabled: true
        },
        {
            lg: { size: 4 },
            label: 'First Name (TH)',
            name: 'first_name_th',
            type: 'text',
            asterisk: true,
            disabled: true
        },
        {
            lg: { size: 6 },
            label: 'Last Name (TH)',
            name: 'last_name_th',
            type: 'text',
            asterisk: true,
            disabled: true
        }
    ],
    [
        {
            lg: { size: 4, offset: 2 },
            label: 'First Name (EN)',
            name: 'first_name_en',
            type: 'text',
            asterisk: true,
            disabled: true
        },
        {
            lg: { size: 6 },
            label: 'Last Name (EN)',
            name: 'last_name_en',
            type: 'text',
            asterisk: true,
            disabled: true
        }
    ],
    [
        {
            label: 'Email',
            name: 'email',
            type: 'email',
            asterisk: true,
            disabled: true
        },
        {
            label: 'Branch',
            name: 'branch',
            type: 'text'
        }
    ],
    [
        {
            label: 'Department',
            name: 'department',
            type: 'text'
        },
        {
            label: 'Position',
            name: 'position',
            type: 'text'
        }
    ]
];

const parseErrorMessage = (email: string, errorMessage: string) => {
    switch (errorMessage) {
        case 'current username is registered':
            return `ผู้ใช้งาน ${email} ได้มีการลงทะเบียนเข้าใช้งานแล้ว ไม่สามารถลงทะเบียนซ้ำได้ หากท่านลืมรหัสการเข้าใช้งาน สามารถ กดปุ่ม "Forget Password" เพื่อตั้งรหัสการเข้าใช้งานใหม่ หรือหากท่านยังไม่ได้ยืนยันการสมัคร กรุณายืนยันการเข้าใช้งานผ่านทาง Email `;
        default:
            return errorMessage;
    }
};

function UserAndEmployeeInformation() {
    const history = useHistory();
    const location = useLocation<RouteState | undefined>();

    const registerStoreState = useSelector<
        ApplicationState,
        Exclude<ApplicationState['register'], undefined>
    >(state => state.register!);
    const dispatch = useDispatch();

    const redirectRef = useRef<string>();
    const [register, setRegister] = useState<Register>();

    const [pending, setPending] = useState(false);
    const [createSuccess, setCreateSuccess] = useState(false);

    // Check necessary values
    useEffect(() => {
        if (
            !location.state ||
            !location.state.redirect ||
            !location.state.modelRegister
        ) {
            if (window.top) window.top.location.replace('/');
            else window.location.replace('/');
            return;
        }

        const { redirect, modelRegister } = location.state;

        redirectRef.current = redirect;
        setRegister({
            ...modelRegister,
            employee_information: {
                email: modelRegister.email,
                user_id: modelRegister.email.split('@')[0],
                prefix: modelRegister.prefix,
                first_name_th: modelRegister.first_name,
                last_name_th: modelRegister.last_name,
                first_name_en: modelRegister.first_name_en,
                last_name_en: modelRegister.last_name_en
            }
        });
    }, [location.state]);

    useEffect(() => {
        const { isLoadingRegister, statusRegister, messageRegister } =
            registerStoreState;

        if (isLoadingRegister && statusRegister === 200) {
            setCreateSuccess(true);
            
            setPending(false);
            hideLoader();
            showSuccessMessage();

            return;
        }

        if (isLoadingRegister && statusRegister !== 200) {
            setPending(false);
            hideLoader();
            
            const error = safeParseJSON<{ message?: string }>(messageRegister ?? '')
            
            const errorMessage = parseErrorMessage(
                register?.email ?? '',
                error?.message ?? ''
            );

            Swal.fire({
                title: 'Error!',
                text: errorMessage,
                icon: 'error',
                showCancelButton: false,
                confirmButtonColor: '#3085d6',
                confirmButtonText: 'ยืนยัน!'
            });

            dispatch(
                // @ts-ignore
                RegisterStore.actionCreators.requestFunction('', 'CLEAR', '')
            );

            return;
        }
    }, [registerStoreState]);

    const handleChangeEmployeeInformation = (key: string, value: string) => {
        if (!register) return;

        const nextRegister = {
            ...register,
            employee_information: {
                ...register.employee_information,
                [key]: value
            }
        };

        setRegister(nextRegister);
    };

    const handleSubmit = async () => {
        if (!register || !register.employee_information) return;

        const { employee_code, user_id, branch, department, position } =
            register.employee_information;

        const trimmedEmployeeCode = employee_code && employee_code.trim();
        const trimmedUserId = user_id && user_id.trim();
        const trimmedBranch = branch && branch.trim();
        const trimmedDepartment = department && department.trim();
        const trimmedPosition = position && position.trim();

        if (!trimmedEmployeeCode) {
            const result = await Swal.fire({
                title: 'รหัสพนักงานว่าง ต้องการใส่รหัสพนักงานหรือไม่?',
                icon: 'warning',
                showDenyButton: true,
                denyButtonText: 'ไม่ต้องการ',
                confirmButtonText: 'ต้องการ'
            });

            if (result.isConfirmed) return;
        }

        // Send information to backend
        const data = {
            ...register,
            employee_information: {
                ...register.employee_information,
                employee_code: trimmedEmployeeCode,
                user_id: trimmedUserId,
                branch: trimmedBranch,
                department: trimmedDepartment,
                position: trimmedPosition
            }
        };

        dispatch(
            RegisterStore.actionCreators.requestFunction(
                data,
                'POST',
                redirectRef.current ?? ''
            )
        );

        // These will stop in useEffect above
        setPending(true);
        showLoader();
    };

    const showSuccessMessage = async () => {
        await Swal.fire({
            title: 'Success!',
            html: 'ลงทะเบียน กรุณายืนยันตัวตนอีกครั้งที่ e-mail นี้<br>Register success, Please authentication e-mail',
            icon: 'success',
            showCancelButton: false,
            confirmButtonColor: '#3085d6',
            confirmButtonText: 'OK',
            allowOutsideClick: false
        });

        // @ts-ignore
        // WTF???
        dispatch(RegisterStore.actionCreators.requestFunction('', 'CLEAR', ''));

        if (window.top) window.top.location.reload();
    };

    return (
        <Container>
            <Row style={{ paddingTop: '20px' }}>
                <Col lg={{ offset: 1, size: 10 }}>
                    <Card>
                        <CardBody>
                            <h1>User & Employee Information</h1>
                            <hr />
                            {FORM_LAYOUT.map((row, rowIndex) => (
                                <Row key={`row-${rowIndex}`}>
                                    {row.map((field, fieldIndex) => (
                                        <Col
                                            key={`field-${rowIndex}-${fieldIndex}`}
                                            lg={field.lg}
                                        >
                                            <FormGroup>
                                                <div className="d-flex">
                                                    <Label>{field.label}</Label>
                                                    {field.asterisk && <span style={{ marginLeft: '2px', color: '#dc2626' }}>*</span>}
                                                </div>
                                                <Input
                                                    // @ts-ignore
                                                    type={field.type}
                                                    name={field.name}
                                                    disabled={field.disabled}
                                                    value={
                                                        register
                                                            ?.employee_information[
                                                            field.name
                                                        ]
                                                    }
                                                    onChange={event =>
                                                        handleChangeEmployeeInformation(
                                                            event.target.name,
                                                            event.target.value
                                                        )
                                                    }
                                                />
                                            </FormGroup>
                                        </Col>
                                    ))}
                                </Row>
                            ))}

                            {createSuccess ? (
                                <Row className="mt-3">
                                    <Col>
                                        <p className="text-primary text-center">
                                            ลงทะเบียนสำเร็จ
                                            กรุณายืนยันตัวตนอีกครั้งที่ e-mail
                                            นี้
                                        </p>
                                    </Col>
                                </Row>
                            ) : (
                                <Row className="mt-3">
                                    <Col>
                                        <button
                                            disabled={pending}
                                            className="mt-3 btn btn-primary btn-block"
                                            type="button"
                                            onClick={history.goBack}
                                            style={{
                                                backgroundColor:
                                                    colors.btnSSO_Blue,
                                                borderColor: colors.btnSSO_Blue
                                            }}
                                        >
                                            Back
                                        </button>
                                    </Col>
                                    <Col>
                                        <button
                                            disabled={pending}
                                            type="submit"
                                            onClick={handleSubmit}
                                            className="mt-3 btn btn-info btn-block"
                                            style={{
                                                backgroundColor:
                                                    colors.btnSSO_Orange,
                                                borderColor:
                                                    colors.btnSSO_Orange
                                            }}
                                        >
                                            <b>Register</b>
                                        </button>
                                    </Col>
                                </Row>
                            )}
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </Container>
    );
}

export default UserAndEmployeeInformation;
